import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { getApiBaseUrl } from '../config/runtime';

export interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  roles: string[];
  emailConfirmed: boolean;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: Date;
}

export interface LoginResult {
  requiresMfa: boolean;
  userId?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  tokens: AuthTokens | null;
  isLoading: boolean;
  isSignedIn: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyMfa: (userId: string, code: string) => Promise<void>;
  register: (email: string, password: string, fullName: string) => Promise<void>;
  logout: () => void;
  refreshAccessToken: () => Promise<void>;
  hasRole: (role: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const STORAGE_KEYS = {
  ACCESS_TOKEN: 'dmo_access_token',
  REFRESH_TOKEN: 'dmo_refresh_token',
  USER: 'dmo_user',
  EXPIRES_AT: 'dmo_expires_at',
};

// Refresh this long before the access token expires.
const REFRESH_AHEAD_MS = 5 * 60 * 1000;
// After a refresh fails for a reason that isn't "this session is over"
// (offline, API restarting), try again after this long.
const REFRESH_RETRY_MS = 30 * 1000;

// localStorage is the one session every tab shares.
const readStoredSession = (): { user: AuthUser; tokens: AuthTokens } | null => {
  const storedUser = localStorage.getItem(STORAGE_KEYS.USER);
  const storedAccessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  const storedRefreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  const storedExpiresAt = localStorage.getItem(STORAGE_KEYS.EXPIRES_AT);
  if (!storedUser || !storedAccessToken || !storedRefreshToken || !storedExpiresAt) {
    return null;
  }
  return {
    user: JSON.parse(storedUser),
    tokens: { accessToken: storedAccessToken, refreshToken: storedRefreshToken, expiresAt: new Date(storedExpiresAt) },
  };
};

// Serializes refreshes across tabs (Web Locks), falling back to running
// unlocked where the API isn't available.
const withRefreshLock = async (fn: () => Promise<void>): Promise<void> => {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    await navigator.locks.request('dmo-token-refresh', fn);
  } else {
    await fn();
  }
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize from localStorage
  useEffect(() => {
    const stored = readStoredSession();
    if (stored) {
      setUser(stored.user);
      setTokens(stored.tokens);
    }

    setIsLoading(false);
  }, []);

  // Another tab refreshed, logged in or logged out: follow it, so this tab
  // never holds a refresh token the server has already rotated away.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== null && !Object.values(STORAGE_KEYS).includes(e.key)) return;
      const stored = readStoredSession();
      setUser(stored?.user ?? null);
      setTokens(stored?.tokens ?? null);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Schedule a refresh 5 minutes before the access token expires. If we're
  // already inside that window (or the token is already expired — e.g. this
  // tab was backgrounded/asleep and timers were throttled), delay clamps to
  // 0 so it fires right away, via a single path instead of a separate
  // immediate-call branch that could double-fire alongside this timer.
  useEffect(() => {
    if (!tokens) return;

    const timeUntilExpiry = new Date(tokens.expiresAt).getTime() - Date.now();
    const delay = Math.max(timeUntilExpiry - REFRESH_AHEAD_MS, 0);

    const timer = setTimeout(() => {
      refreshAccessToken();
    }, delay);

    return () => clearTimeout(timer);
  }, [tokens?.expiresAt]);

  // Browser tabs throttle/suspend timers when backgrounded, and they stop
  // entirely if the machine sleeps — both common during long (>1hr) training
  // runs. Re-check on regaining visibility so a scheduled refresh that was
  // delayed past expiry still happens promptly instead of waiting on the
  // next natural timer tick.
  useEffect(() => {
    function onVisibilityChange() {
      if (document.visibilityState !== 'visible' || !tokens) return;
      const timeUntilExpiry = new Date(tokens.expiresAt).getTime() - Date.now();
      if (timeUntilExpiry < REFRESH_AHEAD_MS) {
        refreshAccessToken();
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [tokens]);

  // Shared by login (non-MFA), verify-mfa, register, and refresh — all of
  // which return the same { accessToken, refreshToken, expiresAt, user } shape.
  const applyAuthResponse = (data: any) => {
    const newUser: AuthUser = {
      id: data.user.id,
      email: data.user.email,
      fullName: data.user.fullName,
      roles: data.user.roles,
      emailConfirmed: data.user.emailConfirmed,
    };

    const newTokens: AuthTokens = {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      expiresAt: new Date(data.expiresAt),
    };

    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(newUser));
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, newTokens.accessToken);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newTokens.refreshToken);
    localStorage.setItem(STORAGE_KEYS.EXPIRES_AT, newTokens.expiresAt.toISOString());

    setUser(newUser);
    setTokens(newTokens);
  };

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    setIsLoading(true);
    setError(null);

    try {
      const apiUrl = getApiBaseUrl();

      const response = await fetch(`${apiUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Login failed');
      }

      const data = await response.json();

      if (data.requiresMfa) {
        return { requiresMfa: true, userId: data.userId };
      }

      applyAuthResponse(data);
      return { requiresMfa: false };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Login failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const verifyMfa = useCallback(async (userId: string, code: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const apiUrl = getApiBaseUrl();

      const response = await fetch(`${apiUrl}/api/auth/login/verify-mfa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, code }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Invalid or expired code');
      }

      const data = await response.json();
      applyAuthResponse(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Invalid or expired code';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const register = useCallback(async (email: string, password: string, fullName: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const apiUrl = getApiBaseUrl();

      const response = await fetch(`${apiUrl}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, fullName }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Registration failed');
      }

      const data = await response.json();
      applyAuthResponse(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearRefreshRetry = () => {
    if (refreshRetryRef.current) clearTimeout(refreshRetryRef.current);
    refreshRetryRef.current = null;
  };
  useEffect(() => clearRefreshRetry, []);

  const logout = useCallback(() => {
    clearRefreshRetry();
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.EXPIRES_AT);

    setUser(null);
    setTokens(null);
    setError(null);
  }, []);

  const refreshInFlightRef = useRef(false);
  const refreshRef = useRef<() => Promise<void>>();

  // The server keeps one refresh token per user and rotates it on every
  // refresh, while every open tab runs its own refresh timer. Spending a
  // token another tab already rotated gets a 401 — which used to log out
  // every tab (logout clears the shared localStorage). So refreshes run one
  // at a time across tabs, a tab first adopts tokens another tab already
  // refreshed, and only a real rejection logs out: a network error or 5xx
  // (laptop just woke, API restarting) keeps the session and retries.
  const refreshAccessToken = useCallback(async () => {
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    clearRefreshRetry();

    try {
      await withRefreshLock(async () => {
        const stored = readStoredSession();
        if (!stored) {
          logout();
          return;
        }
        if (stored.tokens.expiresAt.getTime() - Date.now() > REFRESH_AHEAD_MS) {
          setUser(stored.user);
          setTokens(stored.tokens);
          return;
        }

        const apiUrl = getApiBaseUrl();
        const response = await fetch(`${apiUrl}/api/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            accessToken: stored.tokens.accessToken,
            refreshToken: stored.tokens.refreshToken,
          }),
        });

        if (response.status === 400 || response.status === 401 || response.status === 403) {
          logout();
          return;
        }
        if (!response.ok) {
          throw new Error(`Token refresh failed (${response.status})`);
        }

        applyAuthResponse(await response.json());
      });
    } catch {
      refreshRetryRef.current = setTimeout(() => refreshRef.current?.(), REFRESH_RETRY_MS);
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [logout]);
  refreshRef.current = refreshAccessToken;

  const hasRole = useCallback((role: string): boolean => {
    if (!user?.roles?.length) return false;
    const needle = role.trim().toUpperCase();
    return user.roles.some((r) => r.trim().toUpperCase() === needle);
  }, [user?.roles]);

  const value: AuthContextType = {
    user,
    tokens,
    isLoading,
    isSignedIn: !!user && !!tokens,
    error,
    login,
    verifyMfa,
    register,
    logout,
    refreshAccessToken,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
